import React, { useEffect, useReducer, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";
import {
	Plus,
	Pencil,
	Trash2,
	Workflow,
	Check,
	ChevronRight,
	ChevronLeft,
	Download,
	Upload,
} from "lucide-react";

import openSocket from "../../services/socket-io";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
	DialogDescription,
} from "../../components/ui/dialog";
import {
	Table,
	TableHeader,
	TableBody,
	TableRow,
	TableHead,
	TableCell,
} from "../../components/ui/table";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "../../components/ui/tooltip";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import ImportFlowModal from "../../components/ImportFlowModal";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import useModules from "../../hooks/useModules";

import { TEMPLATES, TEMPLATE_KEYS, getTemplateByKey } from "./templates";

const reducer = (state, action) => {
	if (action.type === "LOAD_FLOWS") {
		const flows = action.payload;
		const newFlows = [];

		flows.forEach((flow) => {
			const index = state.findIndex((f) => f.id === flow.id);
			if (index !== -1) {
				state[index] = flow;
			} else {
				newFlows.push(flow);
			}
		});

		return [...state, ...newFlows];
	}

	if (action.type === "UPDATE_FLOWS") {
		const flow = action.payload;
		const index = state.findIndex((f) => f.id === flow.id);

		if (index !== -1) {
			state[index] = flow;
			return [...state];
		}
		return [flow, ...state];
	}

	if (action.type === "DELETE_FLOW") {
		const flowId = action.payload;
		const index = state.findIndex((f) => f.id === flowId);
		if (index !== -1) {
			state.splice(index, 1);
		}
		return [...state];
	}

	if (action.type === "RESET") {
		return [];
	}

	return state;
};

const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

const Flows = () => {
	const navigate = useNavigate();

	const [flows, dispatch] = useReducer(reducer, []);
	const [loading, setLoading] = useState(false);
	const [newDialogOpen, setNewDialogOpen] = useState(false);
	const [newStep, setNewStep] = useState(1);
	const [selectedTemplate, setSelectedTemplate] = useState(TEMPLATE_KEYS.BRANCO);
	const [newName, setNewName] = useState("");
	const [saving, setSaving] = useState(false);
	const [deletingFlow, setDeletingFlow] = useState(null);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [importDialogOpen, setImportDialogOpen] = useState(false);

	// Exportar/importar é liberado por padrão e pode ser desligado por empresa
	// em Gestão SaaS › Empresas. Com a flag desligada os botões somem; o
	// backend barra de qualquer forma.
	const { temModulo } = useModules();
	const podeExportarImportar = temModulo("flowsExportImport");

	useEffect(() => {
		(async () => {
			setLoading(true);
			try {
				const { data } = await api.get("/flows");
				dispatch({ type: "LOAD_FLOWS", payload: data });
			} catch (err) {
				toastError(err);
			}
			setLoading(false);
		})();
	}, []);

	useEffect(() => {
		const socket = openSocket();

		const onFlow = (data) => {
			if (data.action === "update") {
				dispatch({ type: "UPDATE_FLOWS", payload: data.flow });
			}
			if (data.action === "delete") {
				dispatch({ type: "DELETE_FLOW", payload: +data.flowId });
			}
		};

		socket.on("flow", onFlow);

		return () => {
			try { socket.off("flow", onFlow); } catch (_) {}
		};
	}, []);

	const resetDialogState = () => {
		setNewStep(1);
		setSelectedTemplate(TEMPLATE_KEYS.BRANCO);
		setNewName("");
	};

	const handleDialogOpenChange = (open) => {
		if (!open) resetDialogState();
		setNewDialogOpen(open);
	};

	const handleCreateFlow = async () => {
		if (newName.trim().length < 2) {
			toast.error("Informe um nome para o fluxo.");
			return;
		}
		setSaving(true);
		try {
			const tpl = getTemplateByKey(selectedTemplate);
			const nodes = deepClone(tpl.nodes || []);
			const edges = deepClone(tpl.edges || []);

			const { data } = await api.post("/flows", {
				name: newName.trim(),
				nodes,
				edges,
			});
			toast.success("Fluxo criado com sucesso.");
			handleDialogOpenChange(false);
			try {
				if (selectedTemplate !== "branco") {
					sessionStorage.setItem(
						`tour:${data.id}`,
						selectedTemplate
					);
				}
			} catch {
				/* storage indisponível: segue sem tour */
			}
			navigate(`/flows/${data.id}/edit`);
		} catch (err) {
			toastError(err);
		}
		setSaving(false);
	};

	const handleFlowImportado = (flowNovo, { modo } = {}) => {
		dispatch({ type: "UPDATE_FLOWS", payload: flowNovo });
		if (modo === "criar") {
			navigate(`/flows/${flowNovo.id}/edit`);
		}
	};

	const handleToggleActive = async (flow) => {
		try {
			const { data } = await api.put(`/flows/${flow.id}`, {
				isActive: !flow.isActive,
			});
			dispatch({ type: "UPDATE_FLOWS", payload: data });
		} catch (err) {
			toastError(err);
		}
	};

	// Liberar o fluxo para o atendente é decisão separada de ligá-lo: um fluxo
	// pode estar respondendo sozinho e mesmo assim não aparecer no seletor de
	// quem atende. Quem decide é o administrador, aqui.
	const handleToggleAgentRunnable = async (flow) => {
		try {
			const { data } = await api.put(`/flows/${flow.id}`, {
				agentRunnable: !flow.agentRunnable,
			});
			dispatch({ type: "UPDATE_FLOWS", payload: data });
		} catch (err) {
			toastError(err);
		}
	};

	const handleDeleteFlow = async (flowId) => {
		try {
			await api.delete(`/flows/${flowId}`);
			toast.success("Fluxo excluído com sucesso.");
			dispatch({ type: "DELETE_FLOW", payload: flowId });
		} catch (err) {
			toastError(err);
		}
		setDeletingFlow(null);
	};

	const baixarJsonComoArquivo = (nome, objeto) => {
		const json = JSON.stringify(objeto, null, 2);
		const blob = new Blob([json], { type: "application/json;charset=utf-8" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = nome;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	};

	const slugify = (texto) =>
		String(texto || "fluxo")
			.normalize("NFD")
			.replace(/[\u0300-\u036f]/g, "")
			.toLowerCase()
			.trim()
			.replace(/[^a-z0-9]+/g, "-")
			.replace(/^-+|-+$/g, "") || "fluxo";

	const handleExportFlow = async (flow) => {
		try {
			const { data } = await api.get(`/flows/${flow.id}`);
			const nodes = Array.isArray(data.nodes) ? data.nodes : [];
			const edges = Array.isArray(data.edges) ? data.edges : [];
			const payload = {
				_perabitExport: true,
				_schemaVersion: 1,
				_kind: "flow",
				_exportedAt: new Date().toISOString(),
				name: data.name || flow.name,
				isActive: typeof data.isActive === "boolean" ? data.isActive : false,
				nodes,
				edges,
				_meta: {
					nodesCount: nodes.length,
					edgesCount: edges.length,
				},
			};
			const nomeArquivo = `fluxo-${flow.id}-${slugify(flow.name)}.json`;
			baixarJsonComoArquivo(nomeArquivo, payload);
			toast.success(`Fluxo "${flow.name}" exportado com sucesso.`);
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<MainContainer>
			<ConfirmationModal
				title={
					deletingFlow ? `Excluir o fluxo ${deletingFlow.name}?` : "Excluir fluxo"
				}
				open={confirmOpen}
				onClose={setConfirmOpen}
				onConfirm={() => handleDeleteFlow(deletingFlow.id)}
			>
				Esta ação não pode ser desfeita. O histórico de execuções também será
				removido.
			</ConfirmationModal>

			<Dialog open={newDialogOpen} onOpenChange={handleDialogOpenChange}>
				<DialogContent className="sm:max-w-2xl">
					<DialogHeader>
						<DialogTitle>
							{newStep === 1 && "Novo fluxo · Escolha um modelo"}
							{newStep === 2 && "Novo fluxo · Defina um nome"}
						</DialogTitle>
						{newStep === 1 && (
							<DialogDescription>
								Selecione um modelo para começar ou use{" "}
								<strong>Em branco</strong> para criar do zero.
							</DialogDescription>
						)}
						{newStep === 2 && (
							<DialogDescription>
								Modelo selecionado:{" "}
								<span className="font-semibold">
									{getTemplateByKey(selectedTemplate).nome}
								</span>
							</DialogDescription>
						)}
					</DialogHeader>

					{newStep === 1 && (
						<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
							{TEMPLATES.map((tpl) => {
								const isSelected = selectedTemplate === tpl.key;
								return (
									<button
										key={tpl.key}
										type="button"
										onClick={() => setSelectedTemplate(tpl.key)}
										className={`group relative flex flex-col items-start rounded-xl border-2 p-4 text-left transition-all hover:border-primary/60 hover:bg-primary/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
											isSelected
												? "border-primary bg-primary/5 shadow-sm"
												: "border-border bg-transparent"
										}`}
									>
										{isSelected && (
											<span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground shadow">
												<Check className="h-3.5 w-3.5" />
											</span>
										)}
										<div className="mb-2 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-2xl">
											{tpl.icone}
										</div>
										<div className="font-semibold leading-tight">
											{tpl.nome}
										</div>
										<div className="mt-1 line-clamp-3 text-xs text-muted-foreground">
											{tpl.descricao}
										</div>
										<div className="mt-3 flex items-center gap-2 text-[11px] text-muted-foreground">
											<Badge variant="outline">
												{(tpl.nodes || []).length} nós
											</Badge>
											<Badge variant="outline">
												{(tpl.edges || []).length} conexões
											</Badge>
										</div>
									</button>
								);
							})}
						</div>
					)}

					{newStep === 2 && (
						<div className="space-y-3">
							<div className="space-y-1.5">
								<Label htmlFor="flowName">Nome do fluxo</Label>
								<Input
									id="flowName"
									autoFocus
									value={newName}
									onChange={(e) => setNewName(e.target.value)}
									onKeyDown={(e) => {
										if (e.key === "Enter") handleCreateFlow();
									}}
									placeholder="Ex.: Boas-vindas automático"
								/>
							</div>
							<div className="space-y-3 rounded-lg border bg-muted/40 p-3 text-xs text-muted-foreground">
								<div className="flex items-center gap-2 font-medium text-foreground">
									<span className="text-base">
										{getTemplateByKey(selectedTemplate).icone}
									</span>
									<span>{getTemplateByKey(selectedTemplate).nome}</span>
								</div>
								<p className="leading-relaxed">
									{getTemplateByKey(selectedTemplate).descricao}
								</p>
								<div className="space-y-1.5 rounded-md border bg-background/60 p-3">
									<div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
										📋 Antes de ativar, lembre de:
									</div>
									<ul className="ml-4 list-disc space-y-1">
										{getTemplateByKey(selectedTemplate).checklist?.length ? (
											getTemplateByKey(selectedTemplate).checklist.map(
												(item, idx) => (
													<li key={idx} className="leading-relaxed">
														{item}
													</li>
												)
											)
										) : (
											<li className="leading-relaxed">
												Revise os nós e clique em salvar.
											</li>
										)}
									</ul>
								</div>
								<p className="leading-relaxed">
									Você poderá editar <strong>todos os nós, textos e conexões</strong>
									no editor após criar — nenhuma configuração é definitiva nesta etapa.
								</p>
							</div>
						</div>
					)}

					<DialogFooter className="gap-2">
						{newStep === 2 && (
							<Button
								type="button"
								variant="outline"
								onClick={() => setNewStep(1)}
								disabled={saving}
							>
								<ChevronLeft className="h-4 w-4" />
								Voltar
							</Button>
						)}
						<Button
							type="button"
							variant="outline"
							onClick={() => handleDialogOpenChange(false)}
							disabled={saving}
						>
							Cancelar
						</Button>
						{newStep === 1 && (
							<Button
								type="button"
								onClick={() => setNewStep(2)}
							>
								Continuar
								<ChevronRight className="h-4 w-4" />
							</Button>
						)}
						{newStep === 2 && (
							<Button
								type="button"
								onClick={handleCreateFlow}
								disabled={saving}
							>
								{saving ? "Criando..." : "Criar e abrir editor"}
							</Button>
						)}
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<ImportFlowModal
				open={importDialogOpen}
				onOpenChange={setImportDialogOpen}
				onSucesso={handleFlowImportado}
			/>

			<MainHeader>
				<Title>Fluxos</Title>
				<MainHeaderButtonsWrapper>
					{podeExportarImportar && (
						<Button
							variant="outline"
							onClick={() => setImportDialogOpen(true)}
						>
							<Upload className="h-4 w-4" />
							Importar
						</Button>
					)}
					<Button onClick={() => setNewDialogOpen(true)}>
						<Plus className="h-4 w-4" />
						Novo fluxo
					</Button>
				</MainHeaderButtonsWrapper>
			</MainHeader>

			<div className="flex-1 overflow-auto px-4 pb-4 sm:px-6 sm:pb-6">
				{/* Tabela: do tablet para cima. */}
				<div className="hidden rounded-xl border bg-card sm:block">
					<Table>
						<TableHeader className="sticky top-0 z-10 bg-card">
							<TableRow>
								<TableHead>Nome</TableHead>
								<TableHead className="text-center">Nós</TableHead>
								<TableHead className="text-center">Conexões</TableHead>
								<TableHead className="text-center">Ativo</TableHead>
								<TableHead className="text-center">Disparo manual</TableHead>
								<TableHead>Atualizado em</TableHead>
								<TableHead className="text-center">Ações</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{flows.map((flow) => (
								<TableRow key={flow.id}>
									<TableCell className="font-medium">
										<div className="flex items-center gap-2">
											<Workflow className="h-4 w-4 text-muted-foreground" />
											{flow.name}
										</div>
									</TableCell>
									<TableCell className="text-center">
										<Badge variant="secondary">
											{(flow.nodes || []).length}
										</Badge>
									</TableCell>
									<TableCell className="text-center">
										<Badge variant="outline">
											{(flow.edges || []).length}
										</Badge>
									</TableCell>
									<TableCell className="text-center">
										<div className="flex justify-center">
											<Switch
												checked={!!flow.isActive}
												onCheckedChange={() => handleToggleActive(flow)}
											/>
										</div>
									</TableCell>
									<TableCell className="text-center">
										<div className="flex justify-center">
											<Switch
												checked={!!flow.agentRunnable}
												onCheckedChange={() =>
													handleToggleAgentRunnable(flow)
												}
											/>
										</div>
									</TableCell>
									<TableCell className="text-muted-foreground">
										{flow.updatedAt
											? format(parseISO(flow.updatedAt), "dd/MM/yyyy HH:mm")
											: "-"}
									</TableCell>
									<TableCell>
										<TooltipProvider>
											<div className="flex items-center justify-center gap-1">
												{podeExportarImportar && (
													<Tooltip>
														<TooltipTrigger asChild>
															<Button
																variant="ghost"
																size="icon"
																className="h-8 w-8"
																onClick={() => handleExportFlow(flow)}
															>
																<Download className="h-4 w-4" />
															</Button>
														</TooltipTrigger>
														<TooltipContent>Exportar JSON</TooltipContent>
													</Tooltip>
												)}
												<Tooltip>
													<TooltipTrigger asChild>
														<Button
															variant="ghost"
															size="icon"
															className="h-8 w-8"
															onClick={() =>
																navigate(`/flows/${flow.id}/edit`)
															}
														>
															<Pencil className="h-4 w-4" />
														</Button>
													</TooltipTrigger>
													<TooltipContent>Abrir editor</TooltipContent>
												</Tooltip>
												<Tooltip>
													<TooltipTrigger asChild>
														<Button
															variant="ghost"
															size="icon"
															className="h-8 w-8 text-destructive hover:text-destructive"
															onClick={() => {
																setDeletingFlow(flow);
																setConfirmOpen(true);
															}}
														>
															<Trash2 className="h-4 w-4" />
														</Button>
													</TooltipTrigger>
													<TooltipContent>Excluir</TooltipContent>
												</Tooltip>
											</div>
										</TooltipProvider>
									</TableCell>
								</TableRow>
							))}
							{loading && <TableRowSkeleton columns={7} />}
							{!loading && flows.length === 0 && (
								<TableRow>
									<TableCell
										colSpan={7}
										className="py-10 text-center text-sm text-muted-foreground"
									>
										Nenhum fluxo criado ainda.
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</div>

				{/* Fluxos no celular.
				    O interruptor de ativo fica à vista: ligar e desligar um fluxo
				    é a ação que se faz com pressa, às vezes do telefone, quando
				    um robô está respondendo o que não devia. */}
				<ul className="space-y-2 sm:hidden">
					{flows.map((flow) => (
						<li key={flow.id} className="rounded-xl border bg-card p-3">
							<div className="flex items-start gap-2">
								<Workflow className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />

								<div className="min-w-0 flex-1">
									<p className="truncate font-medium">{flow.name}</p>
									<p className="mt-0.5 text-xs text-muted-foreground">
										{(flow.nodes || []).length} nó(s)
										{flow.updatedAt &&
											` · ${format(parseISO(flow.updatedAt), "dd/MM/yy HH:mm")}`}
									</p>
								</div>

								<div className="flex shrink-0 items-center">
									{podeExportarImportar && (
										<Button
											variant="ghost"
											size="icon"
											className="h-9 w-9"
											aria-label={`Exportar ${flow.name}`}
											onClick={() => handleExportFlow(flow)}
										>
											<Download className="h-4 w-4" />
										</Button>
									)}
									<Button
										variant="ghost"
										size="icon"
										className="h-9 w-9"
										aria-label={`Abrir editor de ${flow.name}`}
										onClick={() => navigate(`/flows/${flow.id}/edit`)}
									>
										<Pencil className="h-4 w-4" />
									</Button>
									<Button
										variant="ghost"
										size="icon"
										className="h-9 w-9 text-destructive hover:text-destructive"
										aria-label={`Excluir ${flow.name}`}
										onClick={() => {
											setDeletingFlow(flow);
											setConfirmOpen(true);
										}}
									>
										<Trash2 className="h-4 w-4" />
									</Button>
								</div>
							</div>

							<div className="mt-2 flex items-center gap-2 border-t pt-2">
								<Switch
									id={`fluxo-ativo-${flow.id}`}
									checked={!!flow.isActive}
									onCheckedChange={() => handleToggleActive(flow)}
								/>
								<label
									htmlFor={`fluxo-ativo-${flow.id}`}
									className="cursor-pointer text-sm text-muted-foreground"
								>
									{flow.isActive ? "Ativo — está respondendo" : "Desligado"}
								</label>
							</div>

							<div className="mt-2 flex items-center gap-2 border-t pt-2">
								<Switch
									id={`fluxo-disparo-${flow.id}`}
									checked={!!flow.agentRunnable}
									onCheckedChange={() => handleToggleAgentRunnable(flow)}
								/>
								<label
									htmlFor={`fluxo-disparo-${flow.id}`}
									className="cursor-pointer text-sm text-muted-foreground"
								>
									{flow.agentRunnable
										? "O atendente pode disparar na conversa"
										: "Só dispara sozinho"}
								</label>
							</div>
						</li>
					))}

					{!loading && flows.length === 0 && (
						<li className="rounded-xl border bg-card px-4 py-10 text-center text-sm text-muted-foreground">
							Nenhum fluxo criado ainda.
						</li>
					)}
					{loading && (
						<li className="rounded-xl border bg-card px-4 py-6 text-center text-sm text-muted-foreground">
							Carregando...
						</li>
					)}
				</ul>
			</div>
		</MainContainer>
	);
};

export default Flows;
